import { Button, ELanguage, Images, Input, Col, Row } from '@microfrontends/react-components'
import { useDebounce } from '@microfrontends/react-components/components/hooks'
import { buildCompanyLink } from '@microfrontends/react-components/components/Utils'
import { useCallback, useEffect, useState } from 'react'
import { slugify } from '../../helpers/common'
import { useStore } from '../../store'
import { suggestCompany } from './api'
import { CompanySearchHeaderWrapper, SearchCompany, ButtonWrapper } from './CompanySearchHeader.css'
import translations from './translation'

const DEBOUNCE_SUGGESTION_KEYWORD = 400

export interface TOption {
    id: any
    name: string
}
interface ICompanySearchHeaderProps {
    langCode: ELanguage
    keywordFromUrl: string
    industriesFromUrl: number[]
    handleSearch: (keyword: string, industries: number[]) => void
    onChangeIndustries: (industries: number[]) => void
}

const CompanySearchHeader = (props: ICompanySearchHeaderProps) => {
    const { langCode, keywordFromUrl, industriesFromUrl, handleSearch, onChangeIndustries } = props
    const { isMobile } = useStore().state
    // Form
    const [keyword, setKeyword] = useState<string>(keywordFromUrl)
    const [selectedIndustries, setSelectedIndustries] = useState<number[]>(industriesFromUrl)
    // End form
    const [companySuggestionList, setCompanySuggestionList] = useState<any[]>([])
    const debouncedContent = useDebounce([keyword], DEBOUNCE_SUGGESTION_KEYWORD)

    const t = useCallback(
        (text: string): string => {
            if (langCode === ELanguage.EN) {
                return text
            } else {
                return translations[text]
            }
        },
        [langCode]
    )

    const handleOnKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch(keyword, selectedIndustries)
        }
    }

    const handleChangeIndustries = (value: any) => {
        setSelectedIndustries(value)
        onChangeIndustries && onChangeIndustries([value])
    }

    useEffect(() => {
        setKeyword(keywordFromUrl)
    }, [keywordFromUrl])

    useEffect(() => {
        setSelectedIndustries(industriesFromUrl)
    }, [industriesFromUrl])

    useEffect(() => {
        ;(async () => {
            if (!debouncedContent) {
                return
            }
            let response = await suggestCompany(keyword, langCode)

            if (!response) {
                response = [
                    {
                        id: 0,
                        name: t('No result'),
                        isCCP: 0,
                        companySlug: ''
                    }
                ]
            }

            setCompanySuggestionList(response)
        })()
    }, [debouncedContent, keyword, langCode, t])

    return (
        <CompanySearchHeaderWrapper>
            <h1>{t('Find Your Best Working Place')}</h1>
            <p>{t('Discover companies from the inside and find your best working place.')}</p>
            <SearchCompany>
                <Row gutter={[0, 8]}>
                    <Col xs={24} sm={20}>
                        <Input
                            type='text'
                            value={keyword}
                            onChange={setKeyword}
                            placeholder={t('Enter company name')}
                            prefixIcon={<Images.SearchIcon />}
                            onKeyUp={handleOnKeyUp}
                            style={{
                                marginRight: 16
                            }}
                            maxLength={150}
                            listRecommend={companySuggestionList}
                            onClickSuggestionItem={(item) => {
                                if (item.id === 0) {
                                    return
                                }
                                let companyUrl = buildCompanyLink(
                                    item.id,
                                    item.companySlug || slugify(item.name),
                                    item.companySlug,
                                    langCode,
                                    item.isCCP
                                )
                                if (typeof window === 'undefined') {
                                    return
                                }
                                window.location.href = companyUrl
                            }}
                            id='company-search-input'
                            enterKeyHint='search'
                        />
                    </Col>
                    {!isMobile && (
                        <ButtonWrapper xs={4}>
                            <Button
                                data-id='btn-search'
                                style={{ width: '100%' }}
                                type='primary'
                                onClick={() => handleSearch(keyword, selectedIndustries)}
                            >
                                {t('Search')}
                            </Button>
                        </ButtonWrapper>
                    )}
                </Row>
            </SearchCompany>
        </CompanySearchHeaderWrapper>
    )
}

export default CompanySearchHeader
